/**
 * EventCollector - ブラウザイベント収集
 *
 * ページビュー、ルート変更、可視性変更などを自動収集
 */

import type {
  TrackedEvent,
  EventType,
  PageViewPayload,
  RouteChangePayload,
  VisibilityChangePayload,
  ErrorPayload,
} from '@common/models';

export type EventCallback = (event: TrackedEvent) => void;

export class EventCollector {
  private trackingUserId: string | null = null;
  private socketSessionId: string | null = null;
  private eventCallback: EventCallback | null = null;
  private listeners: Array<() => void> = [];
  private lastPath: string = '';

  constructor() {
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handlePopState = this.handlePopState.bind(this);
    this.handleError = this.handleError.bind(this);
  }

  /**
   * セッションID設定（ch-serverから受け取ったもの）
   * @param sessionId - セッションID
   */
  setIdentity(trackingUserId: string, socketSessionId?: string): void {
    this.trackingUserId = trackingUserId;
    this.socketSessionId = socketSessionId || null;
  }

  /**
   * イベントコールバック設定
   * @param callback - イベント発生時に呼ばれるコールバック
   */
  setEventCallback(callback: EventCallback): void {
    this.eventCallback = callback;
  }

  /**
   * 自動トラッキング開始
   */
  startAutoTracking(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      console.warn('[EventCollector] Not in browser environment');
      return;
    }

    // 初回ページビュー
    this.trackPageView(window.location.pathname);
    this.lastPath = window.location.pathname;

    // ページ可視性変更
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.listeners.push(() => {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    });

    // ブラウザバック/フォワード（popstate）
    window.addEventListener('popstate', this.handlePopState);
    this.listeners.push(() => {
      window.removeEventListener('popstate', this.handlePopState);
    });

    // エラートラッキング
    window.addEventListener('error', this.handleError);
    this.listeners.push(() => {
      window.removeEventListener('error', this.handleError);
    });

    // SPA対応: pushState/replaceStateをフック
    this.hookHistoryMethods();
  }

  /**
   * 自動トラッキング停止
   */
  stopAutoTracking(): void {
    this.listeners.forEach((cleanup) => cleanup());
    this.listeners = [];
  }

  /**
   * ページビュートラッキング
   * @param path - ページパス
   * @param title - ページタイトル（省略時はdocument.title）
   */
  trackPageView(path: string, title?: string): void {
    if (!this.trackingUserId) {
      console.warn('[EventCollector] No trackingUserId set');
      return;
    }

    const payload: PageViewPayload = {
      type: 'page_view',
      path,
      title: title || (typeof document !== 'undefined' ? document.title : ''),
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    };

    this.emitEvent('page_view', payload);
  }

  /**
   * ルート変更トラッキング
   * @param from - 変更前のパス
   * @param to - 変更後のパス
   */
  trackRouteChange(from: string, to: string): void {
    if (!this.trackingUserId) return;

    const payload: RouteChangePayload = {
      type: 'route_change',
      from,
      to,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    };

    this.emitEvent('route_change', payload);
  }

  /**
   * 可視性変更ハンドラー
   */
  private handleVisibilityChange(): void {
    if (!this.trackingUserId) return;

    const payload: VisibilityChangePayload = {
      type: 'visibility_change',
      hidden: document.hidden,
    };

    this.emitEvent('visibility_change', payload);
  }

  /**
   * popstateハンドラー（ブラウザバック/フォワード）
   */
  private handlePopState(): void {
    const newPath = window.location.pathname;

    if (newPath !== this.lastPath) {
      this.trackRouteChange(this.lastPath, newPath);
      this.trackPageView(newPath);
      this.lastPath = newPath;
    }
  }

  /**
   * エラーハンドラー
   */
  private handleError(event: ErrorEvent): void {
    if (!this.trackingUserId) return;

    const payload: ErrorPayload = {
      type: 'error',
      message: event.message,
      stack: event.error?.stack,
      context: `${event.filename}:${event.lineno}:${event.colno}`,
    };

    this.emitEvent('error', payload);
  }

  /**
   * History API（pushState/replaceState）をフック
   */
  private hookHistoryMethods(): void {
    if (typeof window === 'undefined' || !window.history) return;

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const self = this;

    // pushStateフック
    window.history.pushState = function (state: any, title: string, url?: string | URL | null) {
      const result = originalPushState.apply(window.history, [state, title, url]);

      const newPath = url ? (typeof url === 'string' ? url : url.pathname) : window.location.pathname;

      if (newPath !== self.lastPath) {
        self.trackRouteChange(self.lastPath, newPath);
        self.trackPageView(newPath);
        self.lastPath = newPath;
      }

      return result;
    };

    // replaceStateフック
    window.history.replaceState = function (state: any, title: string, url?: string | URL | null) {
      const result = originalReplaceState.apply(window.history, [state, title, url]);

      const newPath = url ? (typeof url === 'string' ? url : url.pathname) : window.location.pathname;

      if (newPath !== self.lastPath) {
        self.trackPageView(newPath);
        self.lastPath = newPath;
      }

      return result;
    };

    // クリーンアップ関数を追加
    this.listeners.push(() => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    });
  }

  /**
   * イベント発行
   * @param eventType - イベントタイプ
   * @param payload - ペイロード
   */
  private emitEvent(eventType: EventType, payload: any): void {
    if (!this.trackingUserId) {
      console.warn('[EventCollector] Cannot emit event without trackingUserId');
      return;
    }

    const event: TrackedEvent = {
      eventId: this.generateEventId(),
      sessionId: this.trackingUserId,
      trackingUserId: this.trackingUserId,
      socketSessionId: this.socketSessionId || undefined,
      timestamp: Date.now(),
      eventType,
      payload,
    };

    if (this.eventCallback) {
      this.eventCallback(event);
    }
  }

  /**
   * イベントID生成（UUID v4形式）
   * @returns UUID v4
   */
  private generateEventId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // フォールバック: 簡易UUID生成
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
